import { useState, useEffect } from "react";
import { Plus, Trash2, Ticket, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDiscountCodes = () => {
  const [codes, setCodes] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    min_nights: '',
    max_uses: '',
    valid_from: '',
    valid_until: '',
    active: true
  });

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      const response = await axios.get(`${API}/discount-codes`);
      setCodes(response.data);
    } catch (error) {
      console.error("Error fetching discount codes:", error);
      toast.error("Failed to load discount codes");
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'number' ? (value ? parseFloat(value) : '') : value
    });
  };

  const generateRandomCode = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    setFormData({ ...formData, code });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      code: formData.code.toUpperCase(),
      discount_type: formData.discount_type,
      discount_value: parseFloat(formData.discount_value),
      min_nights: formData.min_nights ? parseInt(formData.min_nights) : null,
      max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
      valid_from: formData.valid_from || null,
      valid_until: formData.valid_until || null,
      active: formData.active
    };
    
    try {
      await axios.post(`${API}/discount-codes`, payload);
      toast.success("Discount code created successfully!");
      setIsDialogOpen(false);
      setFormData({
        code: '',
        discount_type: 'percentage',
        discount_value: 10,
        min_nights: '',
        max_uses: '',
        valid_from: '',
        valid_until: '',
        active: true
      });
      fetchCodes();
    } catch (error) {
      console.error("Error creating discount code:", error);
      toast.error(error.response?.data?.detail || "Failed to create discount code");
    }
  };

  const handleToggleActive = async (codeId, currentStatus) => {
    try {
      await axios.patch(`${API}/discount-codes/${codeId}?active=${!currentStatus}`);
      toast.success("Status updated");
      fetchCodes();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (codeId) => {
    if (!window.confirm('Are you sure you want to delete this discount code?')) return;
    
    try {
      await axios.delete(`${API}/discount-codes/${codeId}`);
      toast.success("Discount code deleted");
      fetchCodes();
    } catch (error) {
      console.error("Error deleting code:", error);
      toast.error("Failed to delete discount code");
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success("Code copied to clipboard!");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <AdminLayout>
      <div data-testid="admin-discount-codes">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-primary">
              Discount Codes
            </h1>
            <p className="text-slate-600 mt-2">Create and manage discount codes for your guests</p>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-accent hover:bg-accent/90" data-testid="add-code-button">
                <Plus className="w-4 h-4 mr-2" />
                Create Discount Code
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create Discount Code</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="discount-form">
                <div>
                  <Label htmlFor="code">Discount Code *</Label>
                  <div className="flex gap-2">
                    <Input
                      id="code"
                      name="code"
                      required
                      value={formData.code}
                      onChange={handleInputChange}
                      placeholder="e.g., SUMMER2024"
                      className="uppercase"
                      data-testid="code-input"
                    />
                    <Button type="button" variant="outline" onClick={generateRandomCode}>
                      Generate
                    </Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="discount_type">Discount Type *</Label>
                    <Select 
                      value={formData.discount_type} 
                      onValueChange={(value) => setFormData({ ...formData, discount_type: value })}
                    >
                      <SelectTrigger data-testid="type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage (%)</SelectItem>
                        <SelectItem value="fixed">Fixed Amount (£)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="discount_value">
                      {formData.discount_type === 'percentage' ? 'Percentage (%)' : 'Amount (£)'} *
                    </Label>
                    <Input
                      id="discount_value"
                      name="discount_value"
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      value={formData.discount_value}
                      onChange={handleInputChange}
                      data-testid="value-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="min_nights">Minimum Nights (optional)</Label>
                    <Input
                      id="min_nights"
                      name="min_nights"
                      type="number"
                      min="1"
                      value={formData.min_nights}
                      onChange={handleInputChange}
                      placeholder="No minimum"
                      data-testid="min-nights-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="max_uses">Maximum Uses (optional)</Label>
                    <Input
                      id="max_uses"
                      name="max_uses"
                      type="number"
                      min="1"
                      value={formData.max_uses}
                      onChange={handleInputChange}
                      placeholder="Unlimited"
                      data-testid="max-uses-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="valid_from">Valid From (optional)</Label>
                    <Input
                      id="valid_from"
                      name="valid_from"
                      type="date"
                      value={formData.valid_from}
                      onChange={handleInputChange}
                      data-testid="valid-from-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="valid_until">Valid Until (optional)</Label>
                    <Input
                      id="valid_until"
                      name="valid_until"
                      type="date"
                      value={formData.valid_until}
                      onChange={handleInputChange}
                      data-testid="valid-until-input"
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch
                    id="active"
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                    data-testid="active-switch"
                  />
                  <Label htmlFor="active">Active</Label>
                </div>
                
                <Button type="submit" className="w-full bg-primary" data-testid="submit-code-button">
                  Create Discount Code
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {codes.map((code, index) => (
            <Card key={code.id} data-testid={`code-item-${index}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Ticket className="w-5 h-5 text-accent" />
                      <CardTitle className="text-xl font-mono">{code.code}</CardTitle>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full font-medium inline-block ${
                      code.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {code.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => copyToClipboard(code.code)}
                    data-testid={`copy-code-${index}`}
                  >
                    {copiedCode === code.code ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-slate-600" />
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="bg-accent/10 border border-accent/20 rounded p-3">
                    <p className="text-2xl font-bold text-accent">
                      {code.discount_type === 'percentage' ? `${code.discount_value}% OFF` : `£${code.discount_value} OFF`}
                    </p>
                  </div>
                  
                  <div className="text-sm text-slate-600 space-y-1">
                    {code.min_nights && (
                      <p>Min. {code.min_nights} night{code.min_nights > 1 ? 's' : ''}</p>
                    )}
                    {code.max_uses && (
                      <p>Uses: {code.uses_count} / {code.max_uses}</p>
                    )}
                    {code.valid_until && (
                      <p>Expires: {new Date(code.valid_until).toLocaleDateString()}</p>
                    )}
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    <Switch
                      checked={code.active}
                      onCheckedChange={() => handleToggleActive(code.id, code.active)}
                      data-testid={`toggle-active-${index}`}
                    />
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="ml-auto"
                      onClick={() => handleDelete(code.id)}
                      data-testid={`delete-code-${index}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {codes.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Ticket className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-4">No discount codes yet.</p>
              <p className="text-sm text-slate-500 mb-6">Create discount codes to offer special deals to your guests.</p>
              <Button onClick={() => setIsDialogOpen(true)}>Create Discount Code</Button>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDiscountCodes;
